import React, { useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import MessageContainer from "./MessageContainer";
import { addDays, format } from "date-fns";

import useCommission from "../../../hooks/useCommissions";
import useInvoice from "../../../hooks/useInvoice";

import { ReactComponent as MarkNormalSvg } from "../../../assets/commission/mark-normal.svg";
import { ReactComponent as CancelNormalSvg } from "../../../assets/commission/cancel-normal.svg";
import { ReactComponent as MarkGreenSvg } from "../../../assets/commission/mark-green.svg";

import useChat from "../../../hooks/useChat";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";
import { SocketContext } from "../../../contexts/SocketContext";

const Invoice = ({ id, onPay }) => {
  const { currentSession: session } = useContext(ChatSessionContext);
  const { user, getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const TranslationCommon = getLang(langComponent.common);

  const [detail, setDetail] = useState(undefined);

  const { getCommission } = useCommission();
  const { getInvoice } = useInvoice();
  const { createChat, CHAT_TYPE } = useChat();

  const detailRef = useRef(undefined);

  const { socket } = useContext(SocketContext);

  useEffect(() => {
    if (!id) return;

    let invoice = undefined;

    getInvoice(id)
      .then((_invoice) => {
        invoice = _invoice;
        return getCommission(invoice.commission);
      })
      .then((commission) => {
        invoice.commission = commission;
        invoice.commission.owner = commission.createdBy === user._id;

        _setDetail(invoice);
        socket.on("invoices:active-changed", handleInvoiceActiveChanged);
        socket.on("invoices:status-changed", handleInvoiceStatusChanged);
      })
      .catch((error) => {
        console.error(error.message, message);
      });

    return () => {
      socket.off("invoices:active-changed", handleInvoiceActiveChanged);
      socket.off("invoices:status-changed", handleInvoiceStatusChanged);
    };
  }, []);

  const _setDetail = (detail) => {
    setDetail(detail);
    detailRef.current = detail;
  };

  const getStatus = () => {
    if (!detail.active) {
      return (
        <>
          <CancelNormalSvg />
          <span className="chat-invoice-content-status">
            {Translation("Cancelled")}
          </span>
        </>
      );
    }
    if (detail.paid) {
      return (
        <>
          <MarkGreenSvg />
          <span className="chat-invoice-content-status--paid">
            {Translation("Paid")}
          </span>
        </>
      );
    }

    return null;
  };

  const handleConfirm = async () => {};

  const handleReject = () => rejectQuotation(id);

  const handleInvoiceActiveChanged = async (body) => {
    const invoiceSocket = JSON.parse(body);
    if (invoiceSocket.id !== id) return;

    _setDetail({
      ...detailRef.current,
      active: invoiceSocket.active,
    });
  };

  const handleInvoiceStatusChanged = async (body) => {
    const invoiceSocket = JSON.parse(body);
    if (invoiceSocket.id !== id) return;
    _setDetail({
      ...detailRef.current,
      paid: invoiceSocket.status === "paid",
    });
  };

  if (!detail) {
    return (
      <MessageContainer alignment={"center"}>
        <div className="chat-invoice">Loading</div>
      </MessageContainer>
    );
  }

  const { commission, createdOn } = detail || {};

  return (
    <MessageContainer alignment={"center"}>
      <div className="chat-invoice">
        <div className="chat-invoice-content">
          <h3 className="chat-invoice-content__header">
            {Translation("Invoice")}
          </h3>
          <div className="chat-invoice-content-info">
            <span>{Translation("Order Id")}</span>
            <span className="chat-invoice-content-info__value">
              {commission.no || 1}
            </span>
            <span>{Translation("Created")}</span>
            <span className="chat-invoice-content-info__value">
              {format(new Date(createdOn), "dd MMM yyyy")}
            </span>
            <span>{Translation("Pay within")}</span>
            <span className="chat-invoice-content-info__value">
              {format(addDays(new Date(createdOn), 7), "dd MMM yyyy")}
            </span>
            <span className="chat-invoice-content-info--highlight">
              {Translation("Price")}
            </span>
            <span className="chat-invoice-content-info__value chat-invoice-content-info--highlight">
              THB {(+commission.price).toLocaleString()}
            </span>
          </div>
          {!commission.owner && !detail.paid && (
            <span className="chat-invoice-content__auto-cancel">
              {Translation("*Auto cancel if not paid within 7 days")}
            </span>
          )}
          <div className="chat-invoice-content-cta">
            {!commission.owner && !detail.paid && (
              <div className="chat-invoice-content-cta-action" onClick={onPay}>
                <MarkNormalSvg />
                <span className="chat-invoice-content-cta-action__text">
                  {Translation("Pay")}
                </span>
              </div>
            )}
          </div>
          <div className="chat-invoice-content-status">{getStatus()}</div>
        </div>
      </div>
    </MessageContainer>
  );
};

Invoice.propTypes = {
  onPay: PropTypes.func,
};

Invoice.defaultProps = {
  onPay: () => {},
};

export default Invoice;
